import { useState, type MouseEvent } from "react";
import type { UnlinkTestimonialType } from "@/api/pipeline.service";
import { ConfirmationModal } from "@/components/Modal/ConfirmationModal";
import styles from "./UnlinkLawyerButton.module.css";

type UnlinkLawyerButtonProps = {
  currentType: string | null;
  disabled?: boolean;
  onConfirm: (testimonialType: UnlinkTestimonialType | undefined) => Promise<void>;
};

const TESTIMONIAL_TYPES: { value: UnlinkTestimonialType; label: string }[] = [
  { value: "set_testimonial", label: "Set testimonial" },
  { value: "clerk_testimonial", label: "Clerk testimonial" },
];

export function keepCurrentTypeLabel(currentType: string | null): string {
  const shown = currentType ? currentType.replace("_", " ").toUpperCase() : "unknown";
  return `Keep current type (${shown})`;
}

export const UNLINK_LAWYER_LABEL = "Unlink lawyer from testimonial";
export const UNLINK_LAWYER_TITLE = "Remove lawyer from testimonial";
export const UNLINK_LAWYER_MESSAGE =
  "This cannot be undone. The contact link and the lawyer name are removed from this testimonial and from its translations and copies. The quote will appear on the set page instead of the barrister's page.";
export const UNLINK_LAWYER_CONFIRM = "Remove lawyer";
export const UNLINK_LAWYER_TYPE_HINT = "The quote will be published on the set page.";
export const UNLINK_LAWYER_CHOSEN_TYPE_HINT =
  "The quote will be published on the set page under the chosen type.";
export const UNLINK_LAWYER_TYPE_LEGEND = "Testimonial type";

export function UnlinkLawyerButton({ currentType, disabled, onConfirm }: UnlinkLawyerButtonProps) {
  const [confirming, setConfirming] = useState(false);
  const [testimonialType, setTestimonialType] = useState<UnlinkTestimonialType | undefined>();

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setTestimonialType(undefined);
    setConfirming(true);
  };

  return (
    <>
      <button
        type="button"
        className={styles.unlinkButton}
        title={UNLINK_LAWYER_LABEL}
        aria-label={UNLINK_LAWYER_LABEL}
        disabled={disabled}
        onClick={handleClick}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width={14}
          height={14}
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          aria-hidden
          focusable={false}
        >
          <line x1="6" y1="6" x2="18" y2="18" />
          <line x1="18" y1="6" x2="6" y2="18" />
        </svg>
      </button>

      {confirming && (
        <span onClick={event => event.stopPropagation()}>
          <ConfirmationModal
            title={UNLINK_LAWYER_TITLE}
            message={UNLINK_LAWYER_MESSAGE}
            warningMessage=""
            confirmButtonText={UNLINK_LAWYER_CONFIRM}
            confirmButtonColor="error"
            onClose={() => setConfirming(false)}
            onConfirm={async () => {
              try {
                await onConfirm(testimonialType);
              } finally {
                setConfirming(false);
              }
            }}
          >
            <p className={styles.typeHint}>
              {testimonialType ? UNLINK_LAWYER_CHOSEN_TYPE_HINT : UNLINK_LAWYER_TYPE_HINT}
            </p>
            <fieldset className={styles.typeGroup}>
              <legend className={styles.typeLegend}>{UNLINK_LAWYER_TYPE_LEGEND}</legend>
              <label className={styles.typeOption}>
                <input
                  type="radio"
                  name="unlink-lawyer-testimonial-type"
                  value=""
                  checked={testimonialType === undefined}
                  onChange={() => setTestimonialType(undefined)}
                />
                {keepCurrentTypeLabel(currentType)}
              </label>
              {TESTIMONIAL_TYPES.map(option => (
                <label key={option.value} className={styles.typeOption}>
                  <input
                    type="radio"
                    name="unlink-lawyer-testimonial-type"
                    value={option.value}
                    checked={testimonialType === option.value}
                    onChange={() => setTestimonialType(option.value)}
                  />
                  {option.label}
                </label>
              ))}
            </fieldset>
          </ConfirmationModal>
        </span>
      )}
    </>
  );
}
